import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, LinearProgress, Chip, IconButton, Button, Tooltip, Stack, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, List, ListItem, ListItemText, ListItemIcon,
  ListItemSecondaryAction, CircularProgress, Checkbox, FormControlLabel
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Description as ContractIcon,
  CheckCircle as CheckIcon,
  Cancel as NoFileIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { documentService } from '../../services/api';
import { useConfirm } from '../Common/ConfirmDialog';
import { useToast } from '../Common/ToastProvider';

export default function PersonDocuments({ personId }) {
  const confirm = useConfirm();
  const toast = useToast();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [contractInfo, setContractInfo] = useState(null);
  const [error, setError] = useState(null);
  const [bulkUploadDialog, setBulkUploadDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(null); // document to edit
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  const load = useCallback(async () => {
    if (!personId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [docs, types, contract] = await Promise.all([
        documentService.getPersonDocuments(personId),
        documentService.getRequirements(),
        documentService.getPersonContract(personId)
      ]);
      setDocuments(docs || []);
      setDocumentTypes(types || []);
      setContractInfo(contract || null);
      setError(null);
    } catch (err) {
      console.error('[PersonDocuments] Error:', err);
      setError(t('documents.loadError', 'Failed to load documents'));
    } finally {
      setLoading(false);
    }
  }, [personId, t]);

  useEffect(() => { load(); }, [load]);

  const handleViewContract = async () => {
    try {
      const res = await documentService.getContractUrl(personId);
      // Response interceptor already extracts .data
      window.open(res.sasUrl, '_blank');
    } catch (err) {
      console.error('View contract error:', err);
      toast(t('documents.contractViewError', 'Could not open contract file'), { variant: 'error' });
    }
  };

  // Drag & drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      setBulkUploadDialog(droppedFiles);
    }
  };

  const handleBulkUploadClose = (refresh = false) => {
    setBulkUploadDialog(false);
    if (refresh) load();
  };

  const handleView = async (documentId) => {
    try {
      const res = await documentService.getDocumentUrl(documentId);
      // Response interceptor already extracts .data, so res IS the data
      window.open(res.sasUrl, '_blank');
    } catch (err) {
      console.error('View error:', err);
      toast(t('documents.viewError', 'Неуспешно отваряне на документа'), { variant: 'error' });
    }
  };

  const handleDelete = async (documentId) => {
    if (!(await confirm({ message: t('documents.confirmDelete', 'Delete this document?'), danger: true, confirmLabel: 'Delete' }))) return;
    try {
      await documentService.deleteDocument(documentId);
      load();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleDownload = async (documentId, fileName) => {
    try {
      const res = await documentService.getDocumentUrl(documentId);
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = res.sasUrl;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Download error:', err);
      toast(t('documents.downloadError', 'Неуспешно изтегляне на документа'), { variant: 'error' });
    }
  };

  const handleEditClose = (refresh = false) => {
    setEditDialog(null);
    if (refresh) load();
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Filter documents
  const filteredDocs = documents.filter(doc => {
    const matchesSearch = !searchTerm ||
      doc.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.typeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (doc.customTypeName && doc.customTypeName.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = !filterType || doc.typeId === parseInt(filterType);
    return matchesSearch && matchesType;
  });

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!personId) return <Typography color="text.secondary">{t('documents.noPersonId', 'No person selected')}</Typography>;

  return (
    <Box>
      {/* Contract Section */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <ContractIcon color="primary" sx={{ fontSize: 32 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="medium">
              {t('documents.contract', 'Трудов договор')}
            </Typography>
            {contractInfo?.hasContract ? (
              <Typography variant="body2" color="text.secondary">
                № {contractInfo.contractNumber} / {new Date(contractInfo.contractDate).toLocaleDateString('bg-BG')}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('documents.noContract', 'Няма регистриран договор')}
              </Typography>
            )}
          </Box>
          {contractInfo?.hasContract && (
            <Stack direction="row" spacing={1} alignItems="center">
              {contractInfo.hasFile ? (
                <>
                  <Chip
                    icon={<CheckIcon />}
                    label={t('documents.fileUploaded', 'Качен файл')}
                    color="success"
                    size="small"
                  />
                  <Tooltip title={t('documents.viewContract', 'Преглед на договора')}>
                    <IconButton color="primary" onClick={handleViewContract}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Chip
                  icon={<NoFileIcon />}
                  label={t('documents.noFile', 'Няма качен файл')}
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Toolbar: Search, Filter, Upload button */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder={t('documents.search', 'Search documents...')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
          }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t('documents.filterByType', 'Filter by type')}</InputLabel>
          <Select
            value={filterType}
            label={t('documents.filterByType', 'Filter by type')}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <MenuItem value="">{t('documents.allTypes', 'All types')}</MenuItem>
            {documentTypes.filter(dt => dt.code !== 'OTHERS').map(dt => (
              <MenuItem key={dt.id} value={dt.id}>{dt.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => document.getElementById('bulk-file-input')?.click()}>
          {t('documents.uploadDocument', 'Качване')}
        </Button>
      </Stack>

      {/* Drag & Drop Zone */}
      <Paper
        variant="outlined"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          p: 3,
          mb: 2,
          border: '2px dashed',
          borderColor: isDragOver ? 'primary.main' : 'divider',
          bgcolor: isDragOver ? 'action.hover' : 'background.default',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.light',
            bgcolor: 'action.hover'
          }
        }}
        onClick={() => document.getElementById('bulk-file-input')?.click()}
      >
        <input
          type="file"
          id="bulk-file-input"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
          style={{ display: 'none' }}
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            if (files.length > 0) setBulkUploadDialog(files);
            e.target.value = '';
          }}
        />
        <UploadIcon sx={{ fontSize: 48, color: isDragOver ? 'primary.main' : 'text.disabled', mb: 1 }} />
        <Typography variant="body1" color={isDragOver ? 'primary.main' : 'text.secondary'}>
          {isDragOver
            ? t('documents.dropHere', 'Пуснете файловете тук')
            : t('documents.dragDrop', 'Плъзнете файлове тук или кликнете за избор')}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {t('documents.supportedFormats', 'PDF, JPG, PNG, DOC, DOCX, XLS, XLSX • Множество файлове')}
        </Typography>
      </Paper>

      {/* Documents Table */}
      {filteredDocs.length === 0 ? (
        <Alert severity="info">
          {documents.length === 0
            ? t('documents.noDocuments', 'No documents uploaded yet. Click "Upload Document" to add one.')
            : t('documents.noResults', 'No documents match your search.')}
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('documents.type', 'Type')}</TableCell>
                <TableCell>{t('documents.fileName', 'File Name')}</TableCell>
                <TableCell>{t('documents.size', 'Size')}</TableCell>
                <TableCell>{t('documents.uploadedAt', 'Uploaded')}</TableCell>
                <TableCell>{t('documents.expiryDate', 'Expiry')}</TableCell>
                <TableCell align="right">{t('documents.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDocs.map((doc) => (
                <TableRow key={doc.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{doc.typeName}</Typography>
                      {doc.customTypeName && (
                        <Chip size="small" label={doc.customTypeName} variant="outlined" />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2">{doc.fileName}</Typography>
                      {doc.visibleToEmployee && (
                        <Chip
                          label={t('documents.visibleToEmployeeChip', 'Visible to the employee')}
                          size="small"
                          variant="outlined"
                          color="success"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{formatFileSize(doc.fileSize)}</TableCell>
                  <TableCell>{new Date(doc.uploadedAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {doc.expiryDate ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {doc.isExpired && <WarningIcon color="error" fontSize="small" />}
                        <Typography variant="body2" color={doc.isExpired ? 'error' : 'text.primary'}>
                          {new Date(doc.expiryDate).toLocaleDateString()}
                        </Typography>
                      </Stack>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('documents.view', 'Преглед')}>
                      <IconButton size="small" onClick={() => handleView(doc.id)}><ViewIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={t('documents.download', 'Изтегли')}>
                      <IconButton size="small" onClick={() => handleDownload(doc.id, doc.fileName)}><DownloadIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={t('documents.edit', 'Редактирай')}>
                      <IconButton size="small" onClick={() => setEditDialog(doc)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title={t('documents.delete', 'Изтрий')}>
                      <IconButton size="small" color="error" onClick={() => handleDelete(doc.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Bulk Upload Dialog */}
      <BulkUploadDialog
        open={!!bulkUploadDialog}
        files={bulkUploadDialog || []}
        documentTypes={documentTypes}
        personId={personId}
        onClose={handleBulkUploadClose}
      />

      {/* Edit Document Dialog */}
      <EditDocumentDialog
        open={!!editDialog}
        document={editDialog}
        documentTypes={documentTypes}
        onClose={handleEditClose}
      />
    </Box>
  );
}

function BulkUploadDialog({ open, files, documentTypes, personId, onClose }) {
  const { t } = useTranslation();
  const [fileSettings, setFileSettings] = useState({});
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ uploaded: 0, total: 0 });
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  // Initialize file settings when files change
  useEffect(() => {
    if (files && files.length > 0) {
      const settings = {};
      files.forEach(file => {
        settings[file.name] = {
          typeId: '',
          customTypeName: '',
          expiryDate: '',
          notes: '',
          visibleToEmployee: false,
          removed: false
        };
      });
      setFileSettings(settings);
      setResults(null);
      setError(null);
    }
  }, [files]);

  const updateFileSetting = (fileName, field, value) => {
    setFileSettings(prev => ({
      ...prev,
      [fileName]: { ...prev[fileName], [field]: value }
    }));
  };

  const removeFile = (fileName) => {
    setFileSettings(prev => ({
      ...prev,
      [fileName]: { ...prev[fileName], removed: true }
    }));
  };

  const activeFiles = files.filter(f => !fileSettings[f.name]?.removed);
  const othersType = documentTypes.find(dt => dt.code === 'OTHERS');

  const handleUpload = async () => {
    setUploading(true);
    setError(null);
    setProgress({ uploaded: 0, total: activeFiles.length });

    try {
      const metadata = activeFiles.map(file => {
        const settings = fileSettings[file.name] || {};
        const typeId = settings.typeId ? parseInt(settings.typeId) : othersType?.id;
        const selectedType = documentTypes.find(dt => dt.id === typeId);
        const isOthers = selectedType?.code === 'OTHERS';

        return {
          fileName: file.name,
          typeId: typeId,
          customTypeName: isOthers ? (settings.customTypeName || file.name.replace(/\.[^/.]+$/, '')) : null,
          expiryDate: settings.expiryDate || null,
          notes: settings.notes || null,
          visibleToEmployee: settings.visibleToEmployee || false
        };
      });

      const result = await documentService.uploadDocumentsBulk(personId, activeFiles, metadata);
      setResults(result);
      setProgress({ uploaded: result.uploaded, total: activeFiles.length });

      if (result.failed === 0) {
        // All successful - close after brief delay
        setTimeout(() => onClose(true), 1500);
      }
    } catch (err) {
      console.error('Bulk upload error:', err);
      const apiMessage = err?.response?.data?.message;
      setError(apiMessage || t('documents.bulkUploadError', 'Грешка при качване на файлове'));
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onClose={() => !uploading && onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('documents.bulkUploadTitle', 'Качване на множество документи')}
        <Typography variant="body2" color="text.secondary">
          {activeFiles.length} {t('documents.filesSelected', 'файл(а) избрани')}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {results && (
          <Alert severity={results.failed > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
            {t('documents.uploadComplete', 'Качени')}: {results.uploaded} / {t('documents.failed', 'Грешки')}: {results.failed}
          </Alert>
        )}

        <List dense>
          {files.map((file) => {
            const settings = fileSettings[file.name] || {};
            if (settings.removed) return null;

            const selectedType = documentTypes.find(dt => dt.id === parseInt(settings.typeId));
            const isOthers = selectedType?.code === 'OTHERS' || !settings.typeId;

            return (
              <ListItem
                key={file.name}
                sx={{
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  bgcolor: 'background.default',
                  mb: 1,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <FileIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={file.name}
                    secondary={formatFileSize(file.size)}
                    sx={{ flex: 1 }}
                  />
                  <IconButton size="small" onClick={() => removeFile(file.name)} disabled={uploading}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>

                <Stack direction="row" spacing={2} sx={{ pl: 6 }}>
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>{t('documents.type', 'Тип')}</InputLabel>
                    <Select
                      value={settings.typeId || ''}
                      label={t('documents.type', 'Тип')}
                      onChange={(e) => updateFileSetting(file.name, 'typeId', e.target.value)}
                      disabled={uploading}
                    >
                      <MenuItem value="">{t('documents.selectType', '-- Избери --')}</MenuItem>
                      {documentTypes.map(dt => (
                        <MenuItem key={dt.id} value={dt.id}>{dt.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {isOthers && (
                    <TextField
                      size="small"
                      label={t('documents.customName', 'Име')}
                      value={settings.customTypeName || ''}
                      onChange={(e) => updateFileSetting(file.name, 'customTypeName', e.target.value)}
                      disabled={uploading}
                      placeholder={file.name.replace(/\.[^/.]+$/, '')}
                      sx={{ minWidth: 150 }}
                    />
                  )}

                  <TextField
                    size="small"
                    type="date"
                    label={t('documents.expiry', 'Изтича')}
                    value={settings.expiryDate || ''}
                    onChange={(e) => updateFileSetting(file.name, 'expiryDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={uploading}
                    sx={{ minWidth: 140 }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={settings.visibleToEmployee || false}
                        onChange={(e) => updateFileSetting(file.name, 'visibleToEmployee', e.target.checked)}
                        disabled={uploading}
                      />
                    }
                    label={t('documents.visibleToEmployee', 'Visible to Employee')}
                  />
                </Stack>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={uploading}>
          {t('common.cancel', 'Отказ')}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={uploading || activeFiles.length === 0}
          startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
        >
          {uploading
            ? `${t('documents.uploading', 'Качване')}... (${progress.uploaded}/${progress.total})`
            : t('documents.uploadAll', 'Качи всички')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditDocumentDialog({ open, document, documentTypes, onClose }) {
  const { t } = useTranslation();
  const [fileName, setFileName] = useState('');
  const [typeId, setTypeId] = useState('');
  const [customTypeName, setCustomTypeName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [visibleToEmployee, setVisibleToEmployee] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form when document changes
  useEffect(() => {
    if (document) {
      setFileName(document.fileName || '');
      setTypeId(document.typeId?.toString() || '');
      setCustomTypeName(document.customTypeName || '');
      setExpiryDate(document.expiryDate ? document.expiryDate.split('T')[0] : '');
      setNotes(document.notes || '');
      setVisibleToEmployee(document.visibleToEmployee || false);
      setError(null);
    }
  }, [document]);

  const selectedType = documentTypes.find(dt => dt.id === parseInt(typeId));
  const isOthersType = selectedType?.code === 'OTHERS';

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updateData = {
        fileName: fileName !== document.fileName ? fileName : undefined,
        typeId: typeId ? parseInt(typeId) : undefined,
        customTypeName: isOthersType ? customTypeName : undefined,
        expiryDate: expiryDate || undefined,
        clearExpiryDate: !expiryDate && document.expiryDate ? true : undefined,
        notes: notes !== document.notes ? notes : undefined,
        visibleToEmployee: visibleToEmployee !== document.visibleToEmployee ? visibleToEmployee : undefined
      };

      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === undefined) delete updateData[key];
      });

      await documentService.updateDocument(document.id, updateData);
      onClose(true);
    } catch (err) {
      console.error('Update error:', err);
      const apiMessage = err?.response?.data?.message || err?.response?.data;
      setError(apiMessage || t('documents.updateError', 'Грешка при запазване'));
    } finally {
      setSaving(false);
    }
  };

  if (!document) return null;

  return (
    <Dialog open={open} onClose={() => !saving && onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{t('documents.editDocument', 'Редактиране на документ')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('documents.fileName', 'Име на файла')}
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            fullWidth
            disabled={saving}
          />

          <FormControl fullWidth>
            <InputLabel>{t('documents.type', 'Тип документ')}</InputLabel>
            <Select
              value={typeId}
              label={t('documents.type', 'Тип документ')}
              onChange={(e) => setTypeId(e.target.value)}
              disabled={saving}
            >
              {documentTypes.map(dt => (
                <MenuItem key={dt.id} value={dt.id}>{dt.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {isOthersType && (
            <TextField
              label={t('documents.customTypeName', 'Описание на типа')}
              value={customTypeName}
              onChange={(e) => setCustomTypeName(e.target.value)}
              fullWidth
              disabled={saving}
              required
            />
          )}

          <TextField
            type="date"
            label={t('documents.expiryDate', 'Дата на изтичане')}
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            disabled={saving}
            helperText={t('documents.expiryHelp', 'Оставете празно ако документът няма срок')}
          />

          <TextField
            label={t('documents.notes', 'Бележки')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
            disabled={saving}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={visibleToEmployee}
                onChange={(e) => setVisibleToEmployee(e.target.checked)}
                disabled={saving}
              />
            }
            label={t('documents.visibleToEmployee', 'Visible to Employee')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={saving}>
          {t('common.cancel', 'Отказ')}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !fileName.trim()}
        >
          {saving ? t('common.saving', 'Запазване...') : t('common.save', 'Запази')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
